// /pdks "Kayıtlar" bölümü: kapsamdaki en son giriş-çıkış kayıtları, en yenisi önce.
//
// Çizim (pdks/admin-a.jsx) "bugünün kayıtları"nı canlı akış olarak gösterir.
// GET /v1/time/entries tarih aralığı süzgeci taşımıyor; bu yüzden bölüm "bugün"
// değil "son kayıtlar" der ve keyset ile geriye doğru sayfalar. Süzgeçlerin
// hepsi sunucu parametresidir: yüklenmiş pencereyi süzmek ikinci sayfadaki
// kaydı "yok" diye gösterirdi. Birim, kişi başına mola ve net süre, cihaz/IP
// alanları sözleşmede yok; çizilmedi.

import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { GeofenceResult } from "@novemai/time-attendance-sdk";
import {
  Badge,
  type BadgeTone,
  Button,
  DataTable,
  Empty,
  ErrorState,
  FormField,
  Loading,
  Select,
  type DataTableColumn,
} from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { useTimeZone } from "@/api/use-time-zone";
import { PersonLink } from "@/components/person-link";
import { EmployeeSearchPicker } from "@/features/employees/employee-search-picker";
import type { EmployeeRow } from "@/features/employees/api";
import { formatHours } from "@/lib/hours";

import { formatTimeInstant } from "./format-instant";
import {
  fetchPunchFeedPage,
  TIME_ENTRY_SOURCES,
  TIME_ENTRY_STATUSES,
  type PunchFeedFilters,
  type TimeEntryRow,
  type TimeEntrySource,
  type TimeEntryStatus,
} from "./time-api";
import { ENTRY_STATUS_TONE } from "./time-status";

const ALL = "all";

const GEOFENCE_OUTCOME_TONE: Readonly<Record<GeofenceResult, BadgeTone>> = {
  allowed: "success",
  out_of_bounds: "danger",
  indeterminate: "neutral",
  overridden: "info",
};

function isEntryStatus(value: string): value is TimeEntryStatus {
  return (TIME_ENTRY_STATUSES as readonly string[]).includes(value);
}

function isEntrySource(value: string): value is TimeEntrySource {
  return (TIME_ENTRY_SOURCES as readonly string[]).includes(value);
}

/**
 * @param rowAction the owning screen's per-row action (the manager correction);
 *   the screen keeps the dialog and its pending write so a single owner blocks
 *   navigation while a correction's outcome is unknown.
 */
export function PdksPunchFeed({
  rowAction,
}: {
  readonly rowAction?: (row: TimeEntryRow) => React.ReactNode;
} = {}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const [status, setStatus] = useState<TimeEntryStatus | null>(null);
  const [source, setSource] = useState<TimeEntrySource | null>(null);
  const [employee, setEmployee] = useState<EmployeeRow | null>(null);
  // The picker keeps its own selection; a new key is the only way to clear it.
  const [pickerKey, setPickerKey] = useState(0);

  const filters: PunchFeedFilters = {
    ...(employee !== null && { employeeId: employee.employee_id }),
    ...(status !== null && { status }),
    ...(source !== null && { source }),
  };

  const feed = useKeysetQuery<TimeEntryRow>({
    queryKey: ["time", "entries", "feed", filters],
    fetchPage: (cursor) => fetchPunchFeedPage(filters, cursor),
  });

  const columns: readonly DataTableColumn<TimeEntryRow>[] = [
    {
      id: "employee",
      header: t("time.column.employee"),
      // The id prefix is the fallback for a tombstoned subject only (ADR-011).
      cell: (row): React.ReactNode => (
        <PersonLink employeeId={row.employee_id}>
          {row.employee_name === null ? (
            <span className="font-mono text-[12px] text-[var(--fg-secondary)]">
              {row.employee_id.slice(0, 8)}
            </span>
          ) : (
            <span>{row.employee_name}</span>
          )}
        </PersonLink>
      ),
    },
    {
      id: "start",
      header: t("time.column.start"),
      cell: (row): string => formatTimeInstant(i18n.language, timeZone, row.start_at),
    },
    {
      id: "end",
      header: t("time.column.end"),
      cell: (row): React.ReactNode =>
        row.end_at === null ? (
          <span className="text-[var(--fg-muted)]">{t("time.cell.dash")}</span>
        ) : (
          formatTimeInstant(i18n.language, timeZone, row.end_at)
        ),
    },
    {
      id: "hours",
      header: t("time.column.hours"),
      align: "end",
      cell: (row): React.ReactNode =>
        row.duration_minutes === null ? (
          <span className="text-[var(--fg-muted)]">{t("time.cell.dash")}</span>
        ) : (
          <span className="font-mono tabular-nums">
            {formatHours(row.duration_minutes / 60, i18n.language) ?? t("time.cell.dash")}
          </span>
        ),
    },
    {
      id: "source",
      header: t("time.column.source"),
      cell: (row): string => t(`time.source.${row.source}`),
    },
    {
      id: "location",
      header: t("time.pdks.feed.column.location"),
      // The entry carries no location id or name, only the geofence verdict of
      // each punch point; a punch without one has nothing to show.
      cell: (row): React.ReactNode => {
        const point = row.geofence_match?.clock_in ?? row.geofence_match?.clock_out ?? null;
        if (point === null) {
          return <span className="text-[var(--fg-muted)]">{t("time.cell.dash")}</span>;
        }
        return (
          <Badge tone={GEOFENCE_OUTCOME_TONE[point.outcome]}>
            {t(`time.geofence.outcome.${point.outcome}`)}
          </Badge>
        );
      },
    },
    {
      id: "status",
      header: t("time.column.status"),
      cell: (row): React.ReactNode => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={ENTRY_STATUS_TONE[row.status] ?? "neutral"}>
            {t(`time.entry_status.${row.status}`)}
          </Badge>
          {row.requires_review === true ? (
            <Badge tone="warning">{t("time.pdks.review.quality_flag")}</Badge>
          ) : null}
        </div>
      ),
    },
    ...(rowAction === undefined
      ? []
      : [
          {
            id: "actions",
            header: t("time.column.actions"),
            align: "end" as const,
            cell: (row: TimeEntryRow): React.ReactNode =>
              rowAction(row) ?? <span className="text-[var(--fg-muted)]">{t("time.cell.dash")}</span>,
          },
        ]),
  ];

  const filtered = employee !== null || status !== null || source !== null;

  return (
    <section aria-labelledby="time-punch-feed-heading" className="space-y-3">
      <div>
        <h2 id="time-punch-feed-heading" className="font-serif text-[20px] text-[var(--fg-primary)]">
          {t("time.pdks.feed.title")}
        </h2>
        <p className="mt-1 font-sans text-[14px] leading-relaxed text-[var(--fg-secondary)]">
          {t("time.pdks.feed.description")}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
        <EmployeeSearchPicker
          key={pickerKey}
          name="feed_employee_id"
          labels={{
            label: t("time.pdks.feed.filter.employee"),
            searchLabel: t("time.pdks.feed.filter.employee_search"),
            searchPlaceholder: t("time.pdks.feed.filter.employee_placeholder"),
            choose: t("time.pdks.feed.filter.employee_all"),
            noMatches: t("time.pdks.feed.filter.employee_none"),
            truncated: t("time.pdks.feed.filter.employee_truncated"),
          }}
          onSelect={setEmployee}
        />
        <FormField label={t("time.pdks.feed.filter.status")}>
          <Select
            value={status ?? ALL}
            onChange={(event): void => {
              setStatus(isEntryStatus(event.target.value) ? event.target.value : null);
            }}
          >
            <option value={ALL}>{t("time.pdks.feed.filter.all")}</option>
            {TIME_ENTRY_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`time.entry_status.${value}`)}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label={t("time.pdks.feed.filter.source")}>
          <Select
            value={source ?? ALL}
            onChange={(event): void => {
              setSource(isEntrySource(event.target.value) ? event.target.value : null);
            }}
          >
            <option value={ALL}>{t("time.pdks.feed.filter.all")}</option>
            {TIME_ENTRY_SOURCES.map((value) => (
              <option key={value} value={value}>
                {t(`time.source.${value}`)}
              </option>
            ))}
          </Select>
        </FormField>
        <Button
          variant="ghost"
          size="sm"
          disabled={!filtered}
          onClick={(): void => {
            setEmployee(null);
            setStatus(null);
            setSource(null);
            setPickerKey((key) => key + 1);
          }}
        >
          {t("time.pdks.feed.filter.clear")}
        </Button>
      </div>

      {feed.isLoading ? (
        <Loading label={t("time.pdks.feed.loading")} rows={4} />
      ) : feed.isError ? (
        <ErrorState title={t("time.pdks.feed.error_title")} description={errorMessage(feed.error, t)} />
      ) : feed.items.length > 0 ? (
        <DataTable<TimeEntryRow>
          caption={t("time.pdks.feed.table_caption")}
          columns={columns}
          rows={feed.items}
          rowKey={(row): string => row.entry_id}
          virtualize
          hasNextPage={feed.hasNextPage}
          onLoadMore={feed.fetchNextPage}
          isFetchingNextPage={feed.isFetchingNextPage}
          loadMoreLabel={t("time.load_more")}
        />
      ) : (
        <Empty
          title={filtered ? t("time.pdks.feed.empty_filtered_title") : t("time.pdks.feed.empty_title")}
          description={
            filtered ? t("time.pdks.feed.empty_filtered_description") : t("time.pdks.feed.empty_description")
          }
        />
      )}
    </section>
  );
}
